/* eslint-disable @typescript-eslint/no-shadow */
import { ApiEuTaxonomyRevenueMetrics } from '@upright/common/model/input/eu-taxonomy/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import {
  Bounds,
  CarouselLayout,
  LensBoxMode,
  Side,
} from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { nullBounds, toBounds } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/utils';
import { getEuObjectivesForImpactCategory } from '@upright/ui/common/upright/core/impact-lenses/taxonomy/common/taxonomy-mapping';

interface TaxonomyLensLayout {
  impactEuTaxonomyIndicatorBoxArea: (index: number, side: Side, impact: ImpactCategoryId) => Bounds;
  impactEuTaxonomyIndicatorBoxesArea: (side: Side, impact: ImpactCategoryId) => Bounds;
  infoBoxArea: () => Bounds;
  showFiguresOnDataArea: boolean;
}

export function taxonomyLensLayout(base: CarouselLayout, _data: ApiEuTaxonomyRevenueMetrics): TaxonomyLensLayout {
  const fullWidth = base.fullArea().width;
  const showFiguresOnDataArea = fullWidth > 500;
  const boxesMode = base.boxMode;

  /**
   * Area for symbol indicating whether company has
   * set a target within this category
   */
  function impactEuTaxonomyIndicatorBoxArea(index: number, side: Side, impact: ImpactCategoryId) {
    const da = base.impactDataArea(side, impact);
    const horizontalMargin = 10;
    const height = da.height - 4;
    const spacing = 6;

    function getBoxWidth() {
      if (showFiguresOnDataArea) {
        return impact === ImpactCategoryId.GHG_EMISSIONS ? 64 : 64 * 2 + spacing;
      }
      return impact === ImpactCategoryId.GHG_EMISSIONS ? 22 : 22 * 2 * spacing;
    }

    const boxWidth = getBoxWidth();

    function getXValues() {
      if (side === Side.RIGHT) {
        const x1 = da.x1 + horizontalMargin + (spacing + boxWidth) * index;
        const x2 = x1 + boxWidth;
        return { x1, x2 };
      } else {
        const x2 = da.x2 - horizontalMargin - (spacing + boxWidth) * index;
        const x1 = x2 - boxWidth;
        return { x1, x2 };
      }
    }

    const { x1, x2 } = getXValues();
    const y1 = da.y1 + (da.height - height) / 2;
    const y2 = y1 + height;
    return toBounds({
      x1,
      x2,
      y1,
      y2,
    });
  }

  function impactEuTaxonomyIndicatorBoxesArea(side: Side, impact: ImpactCategoryId) {
    const firstArea = impactEuTaxonomyIndicatorBoxArea(0, side, impact);
    const objectives = getEuObjectivesForImpactCategory(impact);

    const count = Object.keys(objectives).length;
    const lastArea = impactEuTaxonomyIndicatorBoxArea(count - 1, side, impact);

    const x1 = Math.min(firstArea.x1, lastArea.x1);
    const x2 = Math.max(firstArea.x2, lastArea.x2);
    return toBounds({
      x1,
      x2,
      y1: firstArea.y1,
      y2: firstArea.y2,
    });
  }

  const infoBoxHeight = 545;

  function infoBoxArea() {
    if (boxesMode === LensBoxMode.SIDE) {
      const ba = base.boxesArea(Side.RIGHT);
      const y2 = ba.y2;
      const y1 = y2 - infoBoxHeight;
      return toBounds({ x1: ba.x1, x2: ba.x2, y1, y2 });
    }
    return nullBounds;
  }

  return {
    impactEuTaxonomyIndicatorBoxesArea,
    impactEuTaxonomyIndicatorBoxArea,
    infoBoxArea: infoBoxArea,
    showFiguresOnDataArea,
  };
}
